import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, UserCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePageTitle } from "@/hooks/usePageTitle";

export default function CommitteeDetailPage() {
  const { slug } = useParams();
  const [committee, setCommittee] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeBio, setActiveBio] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/content/committees").then(res => res.data.find(c => c.slug === slug)),
      api.get(`/committee-members/by-committee/${slug}`),
    ]).then(([c, m]) => {
      setCommittee(c || null);
      setMembers(m.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);

  usePageTitle(committee?.name);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-24 max-w-7xl">
        <Skeleton className="h-10 w-1/2 mb-6" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-72 rounded-2xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="committee-detail-page">
      {/* Hero */}
      <div className="bg-jci-black relative overflow-hidden">
        {committee?.image_url ? (
          <div className="relative h-[300px] md:h-[400px]">
            <img src={committee.image_url} alt={committee?.name} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-jci-black/60" />
            <div className="absolute inset-0 flex items-center">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">{committee?.name || slug}</h1>
                {committee?.description && <p className="text-white/60 mt-3 max-w-2xl">{committee.description}</p>}
              </div>
            </div>
          </div>
        ) : (
          <div className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">{committee?.name || slug}</h1>
            </div>
          </div>
        )}
        <div className="h-1 bg-gradient-to-r from-jci-blue via-jci-teal to-jci-navy" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-16">
        <Link to="/committees">
          <Button variant="ghost" className="mb-8 text-jci-blue hover:text-jci-navy" data-testid="back-to-committees">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Committees
          </Button>
        </Link>

        {members.length === 0 ? (
          <div className="text-center py-20">
            <UserCircle className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <p className="text-slate-400">Committee members will be added soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8" data-testid="committee-members-grid">
            {members.map((member, i) => (
              <div
                key={member.id}
                className="group relative cursor-pointer"
                onClick={() => setActiveBio(activeBio === i ? null : i)}
                data-testid={`committee-member-${i}`}
              >
                <div className="relative overflow-hidden rounded-2xl">
                  <img
                    src={member.photo_url || "https://via.placeholder.com/400x400?text=Photo"}
                    alt={member.name}
                    className="w-full h-72 object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  {member.bio && (
                    <div className={`absolute inset-0 bg-jci-black/85 flex items-center justify-center p-5 transition-opacity duration-300 ${
                      activeBio === i ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                    }`}>
                      <p className="text-white text-sm text-center leading-relaxed">{member.bio}</p>
                    </div>
                  )}
                </div>
                <div className="mt-4 text-center">
                  <h3 className="font-bold text-jci-black text-base">{member.name}</h3>
                  <p className="text-xs text-slate-500 mt-1">{member.designation}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
